import ButtonDropdown from '@pelckmans/elementis/components/button-dropdown';
import ListGroup from '@pelckmans/elementis/components/list-group';
import Section from '@pelckmans/elementis/components/section';
import Toggle from '@pelckmans/elementis/components/toggle';
import { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import EmptyState from '../../../../../shared-components/EmptyState';
import LoadingState from '../../../../../shared-components/LoadingState';
import MedialinksContext from '../../_contexts/MedialinksContext';
import SelectionContext from '../../_contexts/SelectionContext';
import SlideSetsContext from '../../_contexts/SlideSetsContext';
import UserMaterialContext from '../../_contexts/UserMaterialContext';
import MedialinkList from '../MedialinkList';
import SlideSetList from '../SlideSetList';
import { groupByContentType } from '../utils';
import UserMaterialList from './UserMaterialList';
import MaterialPresentationIcon from '@pelckmans/elementis/icons/material-presentation';
import DuplicateIcon from '@pelckmans/elementis/icons/duplicate';
import MaterialSiteIcon from '@pelckmans/elementis/icons/material-site';
import FileIcon from '@pelckmans/elementis/icons/file';
import MaterialVideoIcon from '@pelckmans/elementis/icons/material-video';
import LinkOnIcon from '@pelckmans/elementis/icons/link-on';
import { ANALYTICS_EVENT_SUBLOCATIONS } from '../../../../../constants/analyticsEvents';
import ModuleContext from '../../../_contexts/ModuleContext';
import TableOfContentsContext from '../../_contexts/TableOfContentsContext';
import useSlideSets from '../../../hooks/useSlideSets';
import { useWithLicenseCheck } from './useWithLicenseCheck';

const BASE = 'PAGES.TOC_LEVEL.SECTIONS.TEACHER.MATERIAL';

export default function MaterialSection() {
  const { t } = useTranslation();
  const [toastId] = useState(() => Date.now());
  const { medialinks, isLoading: isLoadingMediaLinks } = useContext(MedialinksContext);
  const { userMaterial, isLoading: isLoadingUserMaterial } = useContext(UserMaterialContext);
  const { slideSets, isLoading: isLoadingSlideSets } = useContext(SlideSetsContext);
  const { showSelection, toggleShowSelection } = useContext(SelectionContext);
  const { selectedNode } = useContext(TableOfContentsContext);
  const { selectedModule } = useContext(ModuleContext);
  const { buildNewSlideSetUrl, fetchSlideSets } = useSlideSets();
  const { hasLicense, licenseCheck } = useWithLicenseCheck({ id: toastId });

  const handleToggleShowSelection = licenseCheck(toggleShowSelection);

  const handleCopyMaterial = licenseCheck(() => {});
  const handleAddNewURL = licenseCheck(() => {});
  const handleAddNewYoutube = licenseCheck(() => {});
  const handleAddNewFile = licenseCheck(() => {});
  const handleAddNewBookwidget = licenseCheck(() => {});

  useEffect(() => {
    window.addEventListener('visibilitychange', fetchSlideSets);
    return () => window.removeEventListener('visibilitychange', fetchSlideSets);
  }, [fetchSlideSets]);

  if (!selectedNode) return null;

  const medialinksSortedByCategory = groupByContentType(medialinks);

  const isLoading = isLoadingUserMaterial || isLoadingSlideSets || isLoadingMediaLinks;
  const isEmpty = !isLoading && userMaterial.length === 0 && slideSets.length === 0 && medialinks.length === 0;

  const hasContent = !(isEmpty || isLoading);

  const AddUserMaterialButton = (
    <ButtonDropdown
      buttonProps={{ prominence: 'secondary' }}
      title={t(`${BASE}.ADD_USER_MATERIAL_DROPDOWN_BUTTON.TITLE`)}
    >
      <ButtonDropdown.Item
        onClick={handleAddNewURL}
        text={t(`${BASE}.ADD_USER_MATERIAL_DROPDOWN_BUTTON.ITEMS.ADD_NEW_URL`)}
        textIcon={<LinkOnIcon />}
        value={1}
      />
      <ButtonDropdown.Item
        onClick={handleAddNewYoutube}
        text={t(`${BASE}.ADD_USER_MATERIAL_DROPDOWN_BUTTON.ITEMS.ADD_NEW_YOUTUBE`)}
        textIcon={<MaterialVideoIcon />}
        value={2}
      />
      <ButtonDropdown.Item
        onClick={handleAddNewFile}
        text={t(`${BASE}.ADD_USER_MATERIAL_DROPDOWN_BUTTON.ITEMS.ADD_NEW_FILE`)}
        textIcon={<FileIcon />}
        value={3}
      />
      <ButtonDropdown.Item
        onClick={handleAddNewBookwidget}
        text={t(`${BASE}.ADD_USER_MATERIAL_DROPDOWN_BUTTON.ITEMS.ADD_NEW_BOOKWIDGET`)}
        textIcon={<MaterialSiteIcon />}
        value={4}
      />
      <ButtonDropdown.Item
        onClick={handleCopyMaterial}
        text={t(`${BASE}.ADD_USER_MATERIAL_DROPDOWN_BUTTON.ITEMS.COPY_MATERIAL`)}
        textIcon={<DuplicateIcon />}
        value={5}
      />
      {selectedModule?.enableSlideSetsAccess && (
        <ButtonDropdown.Item
          as="link"
          href={
            hasLicense
              ? buildNewSlideSetUrl(selectedModule.id, selectedNode.id, ANALYTICS_EVENT_SUBLOCATIONS.PORTAAL_MATERIAL)
              : ''
          }
          target="_blank"
          rel="noopener noreferrer"
          onClick={licenseCheck()}
          text={t(`${BASE}.ADD_USER_MATERIAL_DROPDOWN_BUTTON.ITEMS.ADD_PRESENTATION`)}
          textIcon={<MaterialPresentationIcon />}
          value={6}
        />
      )}
    </ButtonDropdown>
  );

  return (
    <div className="material-section">
      <Section
        title={t(`${BASE}.TITLE`)}
        actions={hasContent && AddUserMaterialButton}
        toggle={
          hasContent && (
            <Toggle label={t(`${BASE}.TOGGLE_SELECT`)} checked={showSelection} onChange={handleToggleShowSelection} />
          )
        }
        actionGroupRole={hasContent ? 'group' : undefined}
        actionGroupAriaLabel={hasContent ? t(`${BASE}.BUTTON_GROUP.A11Y_LABEL`) : undefined}
      >
        {isLoading && <LoadingState variant="list" aria-label={t(`${BASE}.LOADING.A11Y_LABEL`)} />}

        {!isLoading && isEmpty && (
          <EmptyState
            variant="list"
            headerTitle={t(`${BASE}.EMPTY_DIALOG.TITLE`)}
            bodyText={t(`${BASE}.EMPTY_DIALOG.TEXT`)}
            cta={AddUserMaterialButton}
          />
        )}

        {hasContent && (
          <ListGroup>
            {userMaterial.length > 0 && (
              <UserMaterialList
                key="user-materials"
                userMaterial={userMaterial}
                title={t(`${BASE}.SUBTITLE_OWN_MATERIAL`)}
              />
            )}
            {slideSets.length > 0 && <SlideSetList key="slide-sets" title={t(`${BASE}.SUBTITLE_SLIDE_SETS`)} />}
            {Object.keys(medialinksSortedByCategory).map(category => (
              <MedialinkList key={category} medialinks={medialinksSortedByCategory[category]} title={category} />
            ))}
          </ListGroup>
        )}
      </Section>
    </div>
  );
}
